import { useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  TrendingUp, MessageSquare, Target, Clock, Lock, ArrowRight, Sparkles, RefreshCw,
} from 'lucide-react';
import { useAuthStore } from '@shared/state';
import { getUserLearningPaths } from '@shared/services/learningPath';
import { loadUserContent } from '@shared/services/content';
import { loadRecentDailyActivity } from '@shared/services/gamification';
import { hasFeature } from '@shared/services/featureGating';
import type { SubscriptionTier } from '@shared/constants/tiers';
import type { LearningPath } from '@shared/types/learning-path';
import type { ContentDocument } from '@shared/types/content';
import type { DailyActivityRecord } from '@shared/types/gamification';
import { aggregateConcepts } from '../lib/mastery';
import {
  velocitySeries, tutorSeries, tutorModeDistribution, buildRecommendations,
  type SeriesPoint, type Recommendation,
} from '../lib/analytics-insights';
import { Button } from '../components/ui/Button';

const INDIGO = '#6C5CE7';
const TEAL = '#55EFC4';

function TrendChart({ points, color, label }: { points: SeriesPoint[]; color: string; label: string }) {
  const max = Math.max(1, ...points.map((p) => p.value));
  const w = 100;
  const h = 36;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const coords = points.map((p, i) => [i * step, h - (p.value / max) * h] as const);
  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  const total = points.reduce((s, p) => s + p.value, 0);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs text-content-muted font-body">{label}</span>
        <span className="text-xs text-content-dim">{total} in {points.length}d</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-12">
        <path d={area} fill={color} opacity={0.12} />
        <path d={line} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: typeof Target; label: string; value: string }) {
  return (
    <div className="rounded-xl bg-dark-surface border border-dark-border p-4">
      <Icon size={16} className="text-content-muted mb-2" />
      <p className="font-heading text-2xl font-bold text-content">{value}</p>
      <p className="text-xs text-content-muted mt-0.5">{label}</p>
    </div>
  );
}

const REC_ACCENT: Record<Recommendation['tone'], string> = {
  focus: '#FD79A8',
  continue: INDIGO,
  tutor: '#74B9FF',
  streak: '#F0A500',
};

export function LearningInsightsPage() {
  const user = useAuthStore((s) => s.user);
  const [paths, setPaths] = useState<LearningPath[]>([]);
  const [content, setContent] = useState<ContentDocument[]>([]);
  const [daily, setDaily] = useState<DailyActivityRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const tier = (user?.subscription ?? 'free') as SubscriptionTier;
  const unlocked = hasFeature(tier, 'analytics');

  useEffect(() => {
    if (!user || !unlocked) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getUserLearningPaths(user.uid),
      loadUserContent(user.uid),
      loadRecentDailyActivity(user.uid, 30),
    ])
      .then(([p, c, d]) => {
        if (cancelled) return;
        setPaths(p);
        setContent(c);
        setDaily(d);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, unlocked]);

  const insights = useMemo(() => {
    const concepts = aggregateConcepts(paths, content);
    const sorted = [...concepts].sort((a, b) => b.score - a.score);
    const overall = concepts.length ? concepts.reduce((s, c) => s + c.score, 0) / concepts.length : 0;
    const totalMinutes = paths.reduce((s, p) => s + (p.progress?.totalTimeSpent ?? 0), 0);
    return {
      concepts,
      heat: sorted,
      overall,
      mastered: concepts.filter((c) => c.score >= 80).length,
      totalMinutes,
      velocity: velocitySeries(daily, 30),
      tutor: tutorSeries(daily, 30),
      modes: tutorModeDistribution(daily),
      recommendations: buildRecommendations({ concepts, paths, content, daily }),
    };
  }, [paths, content, daily]);

  if (!user) return <Navigate to="/login" replace />;

  if (!unlocked) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-2xl bg-dark-surface border border-dark-border p-10 text-center">
          <Lock size={36} className="text-content-dim mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold mb-2">Learning Insights</h1>
          <p className="text-sm text-content-muted max-w-md mx-auto mb-5">
            Mastery, learning-velocity trends, tutor-usage patterns and personalised next actions —
            available on Pro and Teams plans.
          </p>
          <Link to="/pricing"><Button variant="gold">Upgrade to unlock</Button></Link>
        </div>
      </main>
    );
  }

  const tutorTotal = insights.tutor.reduce((s, p) => s + p.value, 0);

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <header className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-content">Learning Insights</h1>
        <p className="text-sm font-body text-content-secondary mt-1">
          Mastery, momentum, and where to focus next — over the last 30 days.
        </p>
      </header>

      {loading ? (
        <div className="rounded-2xl bg-dark-surface border border-dark-border p-10 flex items-center justify-center text-content-muted">
          <RefreshCw size={18} className="animate-spin mr-2" /> Loading your insights…
        </div>
      ) : insights.concepts.length === 0 && tutorTotal === 0 ? (
        <div className="rounded-2xl bg-dark-surface border border-dark-border p-10 text-center text-content-muted">
          Complete a checkpoint or ask the tutor a question to start building your insights.
        </div>
      ) : (
        <div className="space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Target} label="Overall mastery" value={`${Math.round(insights.overall)}%`} />
            <StatCard icon={Sparkles} label="Concepts mastered" value={`${insights.mastered}`} />
            <StatCard icon={MessageSquare} label="Tutor questions (30d)" value={`${tutorTotal}`} />
            <StatCard
              icon={Clock}
              label="Time invested"
              value={insights.totalMinutes >= 60 ? `${Math.floor(insights.totalMinutes / 60)}h` : `${insights.totalMinutes}m`}
            />
          </div>

          {/* Trends */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-dark-surface border border-dark-border p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} className="text-content-muted" />
                <h2 className="font-heading text-sm font-semibold">Learning velocity</h2>
              </div>
              <TrendChart points={insights.velocity} color={TEAL} label="Nodes & checkpoints / day" />
            </div>
            <div className="rounded-2xl bg-dark-surface border border-dark-border p-5">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare size={16} className="text-content-muted" />
                <h2 className="font-heading text-sm font-semibold">Tutor usage</h2>
              </div>
              <TrendChart points={insights.tutor} color={INDIGO} label="Questions / day" />
              {insights.modes.length > 0 && (
                <div className="mt-4 space-y-1.5">
                  {insights.modes.map((m) => {
                    const max = Math.max(...insights.modes.map((x) => x.count));
                    return (
                      <div key={m.label} className="flex items-center gap-2">
                        <span className="text-[11px] text-content-muted w-16 shrink-0">{m.label}</span>
                        <div className="flex-1 h-2 rounded-full bg-dark-bg overflow-hidden">
                          <div className="h-full rounded-full bg-indigo-400" style={{ width: `${(m.count / max) * 100}%` }} />
                        </div>
                        <span className="text-[11px] text-content-dim w-6 text-right">{m.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Recommendations */}
          {insights.recommendations.length > 0 && (
            <div className="rounded-2xl bg-dark-surface border border-dark-border p-5">
              <h2 className="font-heading text-sm font-semibold mb-3">Recommended next actions</h2>
              <div className="space-y-2">
                {insights.recommendations.map((r) => (
                  <Link
                    key={r.id}
                    to={r.href}
                    className="flex items-start gap-3 p-3 rounded-xl bg-dark-bg hover:bg-dark-border/40 transition-colors group"
                  >
                    <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ background: REC_ACCENT[r.tone] }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-content">{r.title}</p>
                      <p className="text-xs text-content-muted">{r.detail}</p>
                    </div>
                    <ArrowRight size={15} className="text-content-dim group-hover:text-content mt-1 shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Concept heat map */}
          {insights.heat.length > 0 && (
            <div className="rounded-2xl bg-dark-surface border border-dark-border p-5">
              <h2 className="font-heading text-sm font-semibold mb-3">Concept mastery heat map</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {insights.heat.map((c) => {
                  const colour = c.score >= 80 ? TEAL : c.score >= 50 ? '#F0A500' : '#FD79A8';
                  return (
                    <div
                      key={c.conceptId}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                      style={{ background: `${colour}1f`, borderColor: `${colour}55` }}
                      title={`${c.name}: ${Math.round(c.score)}%`}
                    >
                      <span className="text-xs text-content truncate flex-1">{c.name}</span>
                      <span className="text-xs font-semibold" style={{ color: colour }}>{Math.round(c.score)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
